import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import { PageContainer, Section } from '../components/AdminContentWrapper';
import { DataContext } from '../../../context/DataContext';
import {
  listSignupAllowlist,
  addSignupAllowlistEntry,
  removeSignupAllowlistEntry
} from '../../../services/SignupAllowlistService';
import TablePaginationBar from '../../../components/common/TablePaginationBar';
import { useTablePagination } from '../../../hooks';
import HelpIcon from '../../../components/help/HelpIcon';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const defaultFormState = { email: '', note: '' };

const formatDateTime = (value) => {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
};

const SecuritySignupAllowlist = () => {
  // Solo el grupo Cognito `root` (staff de plataforma) administra esta lista
  // -- NO es una operación por-tenant. `userData` se usa como proxy de
  // "acceso ya verificado" igual que en las otras pantallas de Seguridad.
  const { isPlatformRoot, userData } = useContext(DataContext);
  const accessChecked = Boolean(userData);

  const [formState, setFormState] = useState(defaultFormState);
  const [entries, setEntries] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingEmail, setDeletingEmail] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const { pageRows, paginationProps } = useTablePagination(entries, {
    storageKey: 'signupAllowlist'
  });

  const fetchEntries = useCallback(async () => {
    if (!isPlatformRoot) {
      return;
    }
    setListLoading(true);
    setListError('');
    try {
      const response = await listSignupAllowlist();
      setEntries(Array.isArray(response?.items) ? response.items : []);
    } catch (error) {
      setListError(error?.message || 'No se pudo obtener la lista de correos permitidos.');
    } finally {
      setListLoading(false);
    }
  }, [isPlatformRoot]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const canSubmit = useMemo(
    () => EMAIL_RE.test(formState.email.trim()),
    [formState.email]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setFeedback(null);
    try {
      await addSignupAllowlistEntry({ email: formState.email, note: formState.note });
      setFeedback({ severity: 'success', message: `Se agregó ${formState.email.trim().toLowerCase()} a la lista.` });
      setFormState(defaultFormState);
      await fetchEntries();
    } catch (error) {
      setFeedback({ severity: 'error', message: error?.message || 'No se pudo agregar el correo.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (email) => {
    if (!window.confirm(`¿Quitar ${email} de la lista de correos permitidos?`)) {
      return;
    }
    setDeletingEmail(email);
    setFeedback(null);
    try {
      await removeSignupAllowlistEntry(email);
      setFeedback({ severity: 'success', message: `Se quitó ${email} de la lista.` });
      await fetchEntries();
    } catch (error) {
      setFeedback({ severity: 'error', message: error?.message || 'No se pudo eliminar el correo.' });
    } finally {
      setDeletingEmail(null);
    }
  };

  if (!accessChecked) {
    return (
      <PageContainer
        title="Correos de registro"
        description="Correos que pueden registrarse aunque su dominio no sea corporativo."
      >
        <Section title="Cargando acceso">
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={24} />
            <Typography sx={{ color: 'var(--wb-text-label)' }}>Verificando permisos...</Typography>
          </Stack>
        </Section>
      </PageContainer>
    );
  }

  if (!isPlatformRoot) {
    return (
      <PageContainer
        title="Correos de registro"
        description="Solo el equipo de plataforma (root) puede administrar esta lista."
      >
        <Section title="Acceso restringido">
          <Alert severity="warning" sx={{ backgroundColor: 'var(--wb-warning-12)', color: 'var(--wb-warning)' }}>
            Necesitas privilegios de plataforma (root) para acceder a esta sección.
          </Alert>
        </Section>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title="Correos de registro"
      description="Whitelist de correos exactos que pueden registrarse aunque su dominio sea público (gmail, etc.) o no tenga un TLD corporativo."
      actions={[
        <Button
          key="refresh"
          variant="outlined"
          color="info"
          startIcon={<RefreshIcon />}
          onClick={fetchEntries}
          disabled={listLoading}
          sx={{ textTransform: 'none', borderColor: 'var(--wb-accent-40)', color: 'var(--wb-accent)' }}
        >
          Actualizar
        </Button>
      ]}
    >
      {feedback && (
        <Box sx={{ mb: 3 }}>
          <Alert
            severity={feedback.severity}
            onClose={() => setFeedback(null)}
            sx={{ backgroundColor: 'var(--wb-x-bg-0e749026, rgba(14, 116, 144, 0.15))', color: 'var(--wb-text)' }}
          >
            {feedback.message}
          </Alert>
        </Box>
      )}

      <Section
        title="Agregar correo"
        subtitle="El correo se guarda en minúsculas y se salta el bloqueo de dominios públicos y la exigencia de dominio corporativo."
      >
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '2fr 2fr 120px' } }}
        >
          <TextField
            name="email"
            label="Correo electrónico"
            type="email"
            required
            value={formState.email}
            onChange={handleInputChange}
            placeholder="persona@gmail.com"
            autoComplete="off"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <HelpIcon module="seguridad" topic="correos-registro-permitido" anchor="correo-electrónico" />
                </InputAdornment>
              )
            }}
            sx={{ '& .MuiInputBase-root': { backgroundColor: 'var(--wb-bg-surface)', color: 'var(--wb-text-title)' } }}
          />
          <TextField
            name="note"
            label="Nota (opcional)"
            value={formState.note}
            onChange={handleInputChange}
            placeholder="p. ej. piloto Acme"
            autoComplete="off"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <HelpIcon module="seguridad" topic="correos-registro-permitido" anchor="nota-opcional" />
                </InputAdornment>
              )
            }}
            sx={{ '& .MuiInputBase-root': { backgroundColor: 'var(--wb-bg-surface)', color: 'var(--wb-text-title)' } }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!canSubmit || isSubmitting}
              sx={{ width: '100%', textTransform: 'none', fontWeight: 600 }}
            >
              {isSubmitting ? 'Agregando...' : 'Agregar'}
            </Button>
          </Box>
        </Box>
      </Section>

      <Section title="Correos permitidos" subtitle={`${entries.length} correo(s) en la lista.`}>
        {listError && (
          <Alert
            severity="error"
            sx={{ mb: 2, backgroundColor: 'var(--wb-danger-18)', color: 'var(--wb-danger-soft)' }}
          >
            {listError}
          </Alert>
        )}

        <Box sx={{ position: 'relative', overflowX: 'auto' }}>
          <Table
            size="small"
            sx={{
              minWidth: 720,
              '& th': { color: 'var(--wb-x-text-94a3b8e6, rgba(148, 163, 184, 0.9))', fontWeight: 600, borderBottomColor: 'var(--wb-border)' },
              '& td': { color: 'var(--wb-text)', borderBottomColor: 'var(--wb-border-06)' }
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Correo</TableCell>
                <TableCell>Nota</TableCell>
                <TableCell>Agregado por</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.length === 0 && !listLoading && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography sx={{ color: 'var(--wb-muted-70)', textAlign: 'center', py: 3 }}>
                      No hay correos en la lista.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {pageRows.map((entry) => (
                <TableRow key={entry.email}>
                  <TableCell>{entry.email}</TableCell>
                  <TableCell>{entry.note || '—'}</TableCell>
                  <TableCell>{entry.added_by_email || '—'}</TableCell>
                  <TableCell>{formatDateTime(entry.created_at)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Quitar de la lista">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(entry.email)}
                          disabled={deletingEmail === entry.email}
                          sx={{ color: 'var(--wb-danger)' }}
                        >
                          {deletingEmail === entry.email
                            ? <CircularProgress size={16} />
                            : <DeleteIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {entries.length > 0 && <TablePaginationBar {...paginationProps} />}

          {listLoading && (
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="center"
              sx={{ position: 'absolute', inset: 0, backgroundColor: 'var(--wb-bg-overlay-70)' }}
            >
              <CircularProgress size={24} />
              <Typography sx={{ color: 'var(--wb-text-label)' }}>Actualizando...</Typography>
            </Stack>
          )}
        </Box>
      </Section>
    </PageContainer>
  );
};

export default SecuritySignupAllowlist;
